import PlaceMenu from "../CommonWeb/PlaceMenu";
import GooglePlaces from "../Models/GooglePlaces";

// Per-place photo / Wikipedia info / opening-hours lookups, the same chain
// Build My Trip's enrichment step uses (BuildMyTripWizard.js), as plain
// functions so other importers (Import Itinerary from Image) can share it.

export const ILLUSTRATED_PLACEHOLDER = "https://andiamotravelapp.com/Images/App/IllustratedImage.jpg";
const GOOGLE_PLACES_PHOTO_PATTERN = /places\.googleapis\.com\/v1\/.*\/media/i;

export const hasRealPhoto = (place) =>
  (place.Photo && place.Photo !== ILLUSTRATED_PLACEHOLDER && !GOOGLE_PLACES_PHOTO_PATTERN.test(place.Photo)) ||
  (place.Photo_m && place.Photo_m !== ILLUSTRATED_PLACEHOLDER && !GOOGLE_PLACES_PHOTO_PATTERN.test(place.Photo_m));

export const isBadCoverUrl = (url) => !url || url === ILLUSTRATED_PLACEHOLDER || GOOGLE_PLACES_PHOTO_PATTERN.test(url);

const fetchWithTimeout = (url, ms = 10000) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), ms);
  return fetch(url, { signal: controller.signal }).finally(() => clearTimeout(timeoutId));
};

export const enrichPlacePhoto = (place) =>
  new Promise((resolve) => {
    if (hasRealPhoto(place)) return resolve();
    try {
      PlaceMenu.autoSetPhoto(place, () => resolve());
    } catch (_) {
      resolve();
    }
  });

export const enrichPlaceInfo = async (place) => {
  if (place.Info && place.Info.includes("See more on Wikipedia")) return;
  const query = [place.Name, place.City, place.Country].filter(Boolean).join(", ");
  try {
    const searchUrl = `https://en.wikipedia.org/w/api.php?action=query&list=search&srsearch=${encodeURIComponent(query)}&format=json&origin=*&srlimit=1`;
    const searchData = await (await fetchWithTimeout(searchUrl)).json();
    const results = searchData?.query?.search;
    if (results && results.length > 0) {
      const title = results[0].title;
      const summary = await (await fetchWithTimeout(`https://en.wikipedia.org/api/rest_v1/page/summary/${encodeURIComponent(title)}`)).json();
      const extract = summary.extract || "";
      const pageUrl = summary.content_urls?.desktop?.page || `https://en.wikipedia.org/wiki/${encodeURIComponent(title)}`;
      if (extract) {
        // Keep any note already on the place (day, optional, book ahead).
        const existing = place.Info ? `<p>${place.Info}</p>` : "";
        place.Info = `${existing}${extract}<br><a href="urllink:${pageUrl}">... See more on Wikipedia</a>`;
      }
    }
  } catch (_) {
    // skip on error
  }
};

export const enrichPlaceHours = (place) =>
  new Promise((resolve) => {
    if (GooglePlaces.hasOpeningHoursData(place.OpeningHours)) return resolve();
    try {
      GooglePlaces.fetchOpeningHours(place, (hours) => {
        if (hours) place.OpeningHours = hours;
        resolve();
      });
    } catch (_) {
      resolve();
    }
  });

// Bounded worker pool over `places`; each place gets whichever lookups are
// requested. onProgress(label, done, total) is called as work completes.
export const enrichPlaces = async (places, { photos, info, hours }, onProgress) => {
  const total = places.length;
  let next = 0;
  let done = 0;
  const worker = async () => {
    while (next < total) {
      const place = places[next++];
      if (photos) await enrichPlacePhoto(place);
      if (info) await enrichPlaceInfo(place);
      if (hours) await enrichPlaceHours(place);
      done++;
      if (onProgress) onProgress(done, total);
    }
  };
  await Promise.all(Array.from({ length: Math.min(4, total) }, worker));
};
